import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { SyntheticEvent } from "react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * thumbUrl wraps an upload path in a request to /api/thumb so the browser
 * downloads a small JPEG preview instead of the full-resolution original.
 *
 * Use it for any preview surface (dashboard cards, list thumbnails,
 * scan-screen previews) where the user just needs to recognise the image.
 * Do NOT use it for ML inputs — the ML pipeline must read the original to
 * keep recognition accuracy intact.
 *
 * Pass-through behaviour:
 *   - null/undefined/empty → returns null (caller should render a fallback)
 *   - non-/uploads/ URL (external image) → returns the original unchanged
 *   - already a /api/thumb URL → returns the original unchanged (idempotent)
 */
export function thumbUrl(src: string | null | undefined, width: number): string | null {
  if (!src) return null;
  if (src.startsWith("/api/thumb")) return src;
  if (!src.startsWith("/uploads/")) return src;
  return `/api/thumb?src=${encodeURIComponent(src)}&w=${width}`;
}

/**
 * imgFallbackChain produces an onError handler that walks through a list of
 * fallback URLs in order before giving up. Each handler call advances the
 * cursor by mutating a data-* attribute on the <img>, so a single render is
 * resilient even if the user re-renders. The first non-empty entry is the
 * initial src — the rest are tried in order on error.
 *
 * Used on dashboard cards so a 404 on the thumb endpoint or on the
 * underlying upload doesn't produce a broken-image icon — we fall through
 * to the original, then logo, then hide the <img> entirely.
 */
export function imgFallbackChain(...fallbacks: (string | null | undefined)[]) {
  const chain = fallbacks.filter((u): u is string => !!u);
  return (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const idx = parseInt(img.dataset.fallbackIdx || "0", 10);
    if (idx < chain.length - 1) {
      img.dataset.fallbackIdx = String(idx + 1);
      img.src = chain[idx + 1];
    } else {
      img.style.visibility = "hidden";
    }
  };
}
